import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import api from '../services/api';

const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
    // Check localStorage or system preference
    const [theme, setTheme] = useState(() => {
        if (typeof window !== 'undefined' && window.localStorage) {
            const savedTheme = window.localStorage.getItem('theme');
            if (savedTheme) {
                return savedTheme;
            }
            if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
                return 'dark';
            }
        }
        return 'light'; // Default
    });

    // Track if we've already reported dark mode usage (avoids repeated API calls)
    const darkModeReportedRef = useRef(false);

    useEffect(() => {
        const root = window.document.documentElement;

        // Remove both to start fresh
        root.classList.remove('light', 'dark');

        let resolvedTheme = theme;
        if (theme === 'system') {
            resolvedTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
            root.classList.add(resolvedTheme);
        } else {
            root.classList.add(theme);
        }

        localStorage.setItem('theme', theme);

        // Report first-time dark mode usage for the vol_nuit achievement badge
        // Skip if already reported in a previous session (localStorage flag)
        const darkModeAlreadyRecorded = localStorage.getItem('nizus_dark_mode_recorded') === '1';
        if (resolvedTheme === 'dark' && !darkModeReportedRef.current && !darkModeAlreadyRecorded) {
            darkModeReportedRef.current = true;
            api.post('/user/preferences.php', { hasUsedDarkMode: true })
                .then(() => localStorage.setItem('nizus_dark_mode_recorded', '1'))
                .catch(() => { /* Non-critical */ });
        }
    }, [theme]);

    return (
        <ThemeContext.Provider value={{ theme, setTheme }}>
            {children}
        </ThemeContext.Provider>
    );
};

export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};
